// src/components/artwork/ArtworkAccessManagement.jsx
import React, { useState } from "react";
import { Users, Mail, X, Shield, Clock, UserCheck } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

/**
 * Composant de gestion des accès partagés pour une œuvre
 * Design élégant façon Artsy.com
 * 
 * @param {Object} props
 * @param {Function} props.t - Fonction de traduction i18n
 * @param {Array} props.items - Liste des accès partagés
 * @param {boolean} props.loading - État de chargement
 * @param {string} props.artworkId - ID de l'œuvre
 * @param {Function} props.onAccessChange - Callback quand un accès change
 */
export default function ArtworkAccessManagement({ 
  t, 
  items = [], 
  loading = false,
  artworkId,
  onAccessChange 
}) {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteThirdPartyType, setInviteThirdPartyType] = useState("expert");
  const [inviteMessage, setInviteMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Traduction avec fallback
  const tr = (key, opts) => {
    if (!t || typeof t !== "function") return opts?.defaultValue || key;
    return t(key, opts);
  };

  const handleInvite = async (e) => {
    setError(null);
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSubmitting(true);

    try {
      // Appeler la fonction Postgres
      const { data, error } = await supabase.rpc('invite_expert_to_artwork', {
        p_artwork_id: artworkId,
        p_email: inviteEmail.trim(),
        p_third_party_type: inviteThirdPartyType,
        p_message: inviteMessage || null,
      });

      if (error) {
        throw error;
      }

      if (data && !data.success) {
        throw new Error(data.error || "Erreur lors de l'invitation");
      }

      console.log("[ArtworkAccessManagement] ✅ Invitation réussie:", data);

      // Réinitialiser le formulaire
      setInviteEmail("");
      setInviteMessage("");
      setShowInviteForm(false);

      // Notifier le parent pour recharger
      if (onAccessChange) onAccessChange();

      // Message de succès (optionnel : tu peux utiliser un toast)
      alert(tr("artworkAccess.success.invite", { 
        defaultValue: "Invitation envoyée avec succès !" 
      }));
} catch (err) {
  console.error("[ArtworkAccessManagement] ❌ Erreur invitation:", err);

  const msg = err?.message || String(err);

  // Erreur "expert non Artkiv"
  if (
    msg.includes("n'est pas un expert Artkiv") ||
    msg.includes("Utilisateur avec email") ||
    msg.toLowerCase().includes("introuvable")
  ) {
    setError("NOT_ARTKIV_EXPERT");
  } else {
    setError(msg);
  }
} finally {
  setSubmitting(false);
}
  };

  const handleRevoke = async (accessId, userName) => {
    const confirmed = window.confirm(
      tr("artworkAccess.confirm.revoke", {
        defaultValue: `Révoquer l'accès de ${userName} ?`,
        name: userName,
      })
    );

    if (!confirmed) return;

    try {
      // Soft delete : mettre revoked_at à NOW()
      const { error } = await supabase
        .from("artwork_shares")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", accessId);

      if (error) {
        throw error;
      }

      console.log("[ArtworkAccessManagement] ✅ Accès révoqué:", accessId);

      // Notifier le parent pour recharger
      if (onAccessChange) onAccessChange();

      // Message de succès
      alert(tr("artworkAccess.success.revoke", { 
        defaultValue: "Accès révoqué avec succès" 
      }));
    } catch (err) {
      console.error("[ArtworkAccessManagement] ❌ Erreur révocation:", err);
      alert(
        err.message ||
        tr("artworkAccess.error.revoke", { defaultValue: "Erreur lors de la révocation" })
      );
    }
  };

  return (
    <div className="space-y-8">
      {/* ============================================================ */}
      {/* SECTION 1 : INVITER */}
      {/* ============================================================ */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-serif text-gray-900">
              {tr("artworkAccess.invite.title", { defaultValue: "Partager cette œuvre" })}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {tr("artworkAccess.invite.description", {
                defaultValue: "Invitez des experts, conservateurs ou partenaires à consulter cette fiche",
              })}
            </p>
          </div>

          {!showInviteForm && (
            <button
              onClick={() => setShowInviteForm(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition"
            >
              <Mail className="w-4 h-4" />
              {tr("artworkAccess.invite.button", { defaultValue: "Inviter" })}
            </button>
          )}
        </div>

        {/* Formulaire d'invitation */}
        {showInviteForm && (
          <form onSubmit={handleInvite} className="bg-gray-50 rounded-xl border border-gray-200 p-6">
            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tr("artworkAccess.invite.emailLabel", { defaultValue: "Adresse email" })}
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="expert@example.com"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-black focus:ring-1 focus:ring-black transition text-sm"
                />
              </div>

              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tr("artworkAccess.invite.roleLabel", { defaultValue: "Métier du tiers" })}
                </label>
                <select
                  value={inviteThirdPartyType}
                  onChange={(e) => setInviteThirdPartyType(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-black focus:ring-1 focus:ring-black transition text-sm"
                >
                  <option value="expert">Expert</option>
                  <option value="conservator">Conservateur</option>
                  <option value="insurance">Assureur</option>
                  <option value="viewer">Lecteur</option>
                </select>
              </div>

              {/* Message optionnel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tr("artworkAccess.invite.messageLabel", {
                    defaultValue: "Message (optionnel)",
                  })}
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder={tr("artworkAccess.invite.messagePlaceholder", {
                    defaultValue: "Ajoutez un message personnalisé...",
                  })}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-black focus:ring-1 focus:ring-black transition text-sm resize-none"
                />
              </div>
{/* Erreur "non expert Artkiv" + CTA collaborateur */}
{error === "NOT_ARTKIV_EXPERT" && (
  <div className="p-4 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 space-y-2">
    <div className="font-medium">
      Cette adresse n’est pas associée à un expert Artkiv.
    </div>

    <div className="text-gray-600">
      Pour garantir la confidentialité et la traçabilité, le partage d’œuvre est réservé aux experts vérifiés de la plateforme.
    </div>

    <div className="text-gray-600">
      Vous pouvez inviter cette personne comme <b>collaborateur</b> (lecture seule). L’invité ne paie rien: c’est inclus dans votre espace.
    </div>

    <div className="flex gap-2 pt-2">
      <button
        type="button"
        onClick={() => {
          const email = inviteEmail?.trim();
          if (!email) return;

          const payload = {
            email,
            thirdPartyType: inviteThirdPartyType,
            origin: "artwork_access",
            artworkId,
          };

          // Live event (si CollaboratorsManager est monté)
          window.dispatchEvent(
            new CustomEvent("artkiv:invite-collaborator", { detail: payload })
          );

          // Fallback (si l'utilisateur ouvre Collaborateurs après)
          try {
            localStorage.setItem(
              "artkiv_pending_collaborator_invite",
              JSON.stringify(payload)
            );
          } catch (e) {}

          setError(null);
          setShowInviteForm(false);
        }}
        className="px-4 py-2 bg-black text-white rounded-lg text-xs font-medium"
      >
        Inviter comme collaborateur
      </button>

      <button
        type="button"
        onClick={() => setError(null)}
        className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium"
      >
        Fermer
      </button>
    </div>
  </div>
)}

{/* Erreur générique */}
{error && error !== "NOT_ARTKIV_EXPERT" && (
  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
    {error}
  </div>
)}
              {/* Boutons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
                >
                  {submitting
                    ? tr("artworkAccess.invite.sending", { defaultValue: "Envoi..." })
                    : tr("artworkAccess.invite.send", { defaultValue: "Envoyer l'invitation" })}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteEmail("");
                    setInviteMessage("");
		    setError(null);
                  }}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
                >
                  {tr("common.cancel", { defaultValue: "Annuler" })}
                </button>
              </div>
            </div>
          </form>
        )}
      </section>

      {/* ============================================================ */}
      {/* SECTION 2 : LISTE DES ACCÈS ACTIFS */}
      {/* ============================================================ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-serif text-gray-900">
            {tr("artworkAccess.active.title", { defaultValue: "Accès actifs" })}
          </h3>
          {items.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
              {items.length}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <Shield className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              {tr("artworkAccess.active.empty", {
                defaultValue: "Aucun accès partagé",
              })}
            </p>
            <p className="text-xs text-gray-500">
              {tr("artworkAccess.active.emptyDescription", {
                defaultValue: "Cette œuvre n'est partagée avec personne pour le moment",
              })}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const isExpert = item.shared_with_role?.toLowerCase() === "expert";
              const isConservator = item.shared_with_role?.toLowerCase() === "conservator";
              const isFromCollection = item.access_type === "collection";

              // 🔍 DEBUG
              console.log("[ArtworkAccessManagement] Item:", item);
              console.log("[ArtworkAccessManagement] access_type:", item.access_type);
              console.log("[ArtworkAccessManagement] isFromCollection:", isFromCollection);

              return (
                <div
                  key={item.id}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition ${
                    isFromCollection
                      ? "border-blue-200 bg-blue-50/30"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  {/* Avatar / Icône */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    {isExpert ? (
                      <UserCheck className="w-5 h-5 text-gray-600" />
                    ) : isConservator ? (
                      <Shield className="w-5 h-5 text-gray-600" />
                    ) : (
                      <Users className="w-5 h-5 text-gray-600" />
                    )}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {item.shared_with_name || item.shared_with_email || "Utilisateur"}
                        </p>
                        {item.shared_with_email && (
                          <p className="text-xs text-gray-500 mt-0.5">{item.shared_with_email}</p>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex items-center gap-2">
                        {/* Badge "Via Collection" si hérité */}
                        {isFromCollection && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium whitespace-nowrap">
                            {tr("artworkAccess.active.viaCollection", {
                              defaultValue: "Via Collection",
                            })}
                          </span>
                        )}
                        
                        {/* Badge rôle */}
                        {item.shared_with_role && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                            {item.shared_with_role}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Métadonnées */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      {item.shared_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {tr("artworkAccess.active.sharedAt", { defaultValue: "Partagé" })}{" "}
                          {new Date(item.shared_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      )}

                      {item.can_edit && (
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {tr("artworkAccess.active.canEdit", { defaultValue: "Peut modifier" })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bouton Révoquer - Désactivé si accès collection */}
                  {isFromCollection ? (
                    <div className="flex-shrink-0 p-2 text-gray-400" title={tr("artworkAccess.active.cannotRevokeCollection", { defaultValue: "Accès géré au niveau collection" })}>
                      <Shield className="w-4 h-4" />
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        handleRevoke(item.id, item.shared_with_name || item.shared_with_email)
                      }
                      className="flex-shrink-0 p-2 rounded-lg border border-gray-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition text-gray-500"
                      title={tr("artworkAccess.active.revoke", { defaultValue: "Révoquer l'accès" })}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}